import { FormType } from "./components/types";
import { Branding, UrlFilterDto } from "./shared/types/commonTypes";
import DOMPurify from "dompurify";
import { GlobalVars } from "./global";
import { TFunction } from "i18next";
import { Language } from "./shared/types/websiteTypes";
import { WebsiteLogo } from "./hooks/useSettings";
import { isUSA } from "./constants/env";

export const transformFormsToUrlFilterDto = (
  forms: FormType[],
): UrlFilterDto => {
  let result: UrlFilterDto = {};
  forms.forEach(form => {
    if (form.control === "beginsWith") {
      result.includeUrlBeginsWith =
        form.include.length > 0 ? form.include : undefined;
      result.excludeUrlBeginsWith =
        form.excludes.length > 0 ? form.excludes : undefined;
    }
    if (form.control === "contains") {
      result.includeUrlContains =
        form.include.length > 0 ? form.include : undefined;
      result.excludeUrlContains =
        form.excludes.length > 0 ? form.excludes : undefined;
    }
    if (form.control === "isEqual") {
      result.includeUrlIsEqual =
        form.include.length > 0 ? form.include : undefined;
      result.excludeUrlIsEqual =
        form.excludes.length > 0 ? form.excludes : undefined;
    }
  });
  return result;
};

export const transformUrlFilterToForms = (
  urlFilter: UrlFilterDto,
): FormType[] => {
  const result: FormType[] = [];
  if (urlFilter.includeUrlBeginsWith || urlFilter.excludeUrlBeginsWith) {
    result.push({
      control: "beginsWith",
      include: urlFilter.includeUrlBeginsWith
        ? urlFilter.includeUrlBeginsWith
        : [],
      excludes: urlFilter.excludeUrlBeginsWith
        ? urlFilter.excludeUrlBeginsWith
        : [],
    });
  }
  if (urlFilter.includeUrlContains || urlFilter.excludeUrlContains) {
    result.push({
      control: "contains",
      include: urlFilter.includeUrlContains ? urlFilter.includeUrlContains : [],
      excludes: urlFilter.excludeUrlContains
        ? urlFilter.excludeUrlContains
        : [],
    });
  }
  if (urlFilter.includeUrlIsEqual) {
    result.push({
      control: "isEqual",
      include: urlFilter.includeUrlIsEqual ? urlFilter.includeUrlIsEqual : [],
      excludes: urlFilter.excludeUrlIsEqual ? urlFilter.excludeUrlIsEqual : [],
    });
  }
  return result;
};

export const getPureString = (value: string | Node) =>
  DOMPurify.sanitize(value, { USE_PROFILES: { html: true, svg: true } });

export const emptyNotificationSettings = {
  campaign: {
    notificationVisualSettings: {
      roundness: 0,
    },
  },
  settings: {
    notificationVisualSettings: {
      backgroundColor: "",
      textColor: "",
      iconBackgroundColor: "",
      iconColor: "",
      icon: "",
      roundness: 0,
      linkTextColor: "",
      linkButtonBackgroundColor: "",
      backgroundOpacity: 1,
      activeSize: "classic" as const,
      isShowingIcon: true,
      iconBackgroundRadius: 0,
    },
  },
};

export const generateNotificationPreviews = async (
  t: TFunction<"translation", undefined, "translation">,
  languageString: string,
  branding?: Branding,
  customWebBranding?: WebsiteLogo,
) => {
  const language = convertStringToLanguage(languageString);

  const localNotificationSettings = {
    campaign: {
      notificationVisualSettings: {
        roundness: 0,
      },
    },
    settings: {
      notificationVisualSettings: {
        backgroundColor: branding?.backgroundColor ?? "",
        textColor: branding?.textColor ?? "",
        iconBackgroundColor: branding?.iconBackgroundColor ?? "",
        iconColor: branding?.iconColor ?? "",
        icon: "",
        roundness: branding?.borderRadius ?? 0,
        linkTextColor: "",
        linkButtonBackgroundColor: "",
        backgroundOpacity: branding?.backgroundOpacity ?? 1,
        activeSize: branding?.activeSize ?? "classic",
        isShowingIcon: branding?.brandingIsShowingIcon ?? true,
        iconBackgroundRadius: branding?.iconBackgroundRadius ?? 0,
        customWebBranding,
      },
    },
  };
  const result = {
    lastOrders: "",
    lastRegistrations: "",
    actualVisitors: "",
    visitorSummary: "",
    registrationSummary: "",
    orderSummary: "",
    heureka: "",
    zboziCompanyRating: "",
    zboziProductRating: "",
    individual: "",
    shoptetLastOrders: "",
    shopifyLastOrders: "",
    eshopRychleLastOrders: "",
    shoptetCouponCode: "",
    spolehlivaRecenzeShopRating: "",
    spolehlivaRecenzeProductRating: "",
    googleReviews: "",
    trustpilotReviews: "",
  };

  const lastOrders = await window.getNotificationPreview(
    localNotificationSettings,
    "lastOrders",
    t("campaign.notifications.lastOrders.previewText1") ?? "",
    t("campaign.notifications.lastOrders.previewText2") ?? "",
    undefined,
    language,
  );

  const lastRegistrations = await window.getNotificationPreview(
    localNotificationSettings,
    "lastRegistrations",
    t("campaign.notifications.lastRegistrations.previewText1") ?? "",
    t("campaign.notifications.lastRegistrations.previewText2") ?? "",
    undefined,
    language,
  );

  const actualVisitors = await window.getNotificationPreview(
    localNotificationSettings,
    "actualVisitors",
    undefined,
    t("campaign.notifications.actualVisitors.previewText1") ?? "",
    10,
    language,
  );

  const visitorSummary = await window.getNotificationPreview(
    localNotificationSettings,
    "visitorSummary",
    undefined,
    t("campaign.notifications.visitorSummary.previewText1") ?? "",
    undefined,
    language,
    4,
    10,
  );

  const registrationSummary = await window.getNotificationPreview(
    localNotificationSettings,
    "registrationSummary",
    undefined,
    t("campaign.notifications.visitorSummary.previewText2") ?? "",
    undefined,
    language,
    3,
    10,
  );

  const orderSummary = await window.getNotificationPreview(
    localNotificationSettings,
    "orderSummary",
    undefined,
    t("campaign.notifications.visitorSummary.previewText2") ?? "",
    undefined,
    language,
    2,
    10,
  );

  const heureka = await window.getNotificationPreview(
    localNotificationSettings,
    "heureka",
    undefined,
    undefined,
    undefined,
    language,
    undefined,
    undefined,
    t("campaign.notifications.heureka.previewText1") ?? "",
    4 * 20,
  );

  const zboziCompanyRating = await window.getNotificationPreview(
    localNotificationSettings,
    "zboziCompanyRating",
    undefined,
    undefined,
    undefined,
    language,
    undefined,
    undefined,
    t("campaign.notifications.zbozi.previewText1") ?? "",
    4 * 20,
  );

  const zboziProductRating = await window.getNotificationPreview(
    localNotificationSettings,
    "zboziProductRating",
    undefined,
    undefined,
    undefined,
    language,
    undefined,
    undefined,
    t("campaign.notifications.zbozi.previewText1") ?? "",
    4 * 20,
  );

  const spolehlivaRecenzeShopRating = await window.getNotificationPreview(
    localNotificationSettings,
    "spolehlivaRecenzeShopRating",
    undefined,
    undefined,
    undefined,
    language,
    undefined,
    undefined,
    t("campaign.notifications.zbozi.previewText1") ?? "",
    4,
  );

  const spolehlivaRecenzeProductRating = await window.getNotificationPreview(
    localNotificationSettings,
    "spolehlivaRecenzeProductRating",
    undefined,
    undefined,
    undefined,
    language,
    undefined,
    undefined,
    t("campaign.notifications.zbozi.previewText1") ?? "",
    4,
  );

  const individual = await window.getNotificationPreview(
    localNotificationSettings,
    "individual",
    undefined,
    t("campaign.notifications.individual.previewText1") ?? "",
    undefined,
    language,
    undefined,
    undefined,
    undefined,
    undefined,
    true,
    t("campaign.notifications.individual.previewText2") ?? "",
    false,
  );

  const shoptetLastOrders = await window.getNotificationPreview(
    localNotificationSettings,
    "shoptetLastOrders",
    t("campaign.notifications.lastOrders.previewText1") ?? "",
    t("campaign.notifications.shoptetLastOrders.screwdriver") ?? "",
    undefined,
    language,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    "https://cdn.shopify.com/static/sample-images/bath_grande.jpeg",
  );

  const shoptetCouponCode = await window.getNotificationPreview(
    localNotificationSettings,
    "shoptetCouponCode",
    undefined,
    undefined,
    undefined,
    language,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    t("campaign.notifications.shoptetCouponCode.sale10") ?? "",
    10,
    "%",
    t("campaign.notifications.shoptetCouponCode.wholeSale") ?? "",
  );

  const shopifyLastOrders = await window.getNotificationPreview(
    localNotificationSettings,
    "shopifyLastOrders",
    t("campaign.notifications.lastOrders.previewText1") ?? "",
    t("campaign.notifications.shopifyLastOrders.screwdriver") ?? "",
    undefined,
    language,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    "https://cdn.shopify.com/static/sample-images/bath_grande.jpeg",
  );

  const eshopRychleLastOrders = await window.getNotificationPreview(
    localNotificationSettings,
    "eshopRychleLastOrders",
    t("campaign.notifications.lastOrders.previewText1") ?? "",
    t("campaign.notifications.eshopRychleLastOrders.screwdriver") ?? "",
    undefined,
    language,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    "https://cdn.shopify.com/static/sample-images/bath_grande.jpeg",
  );

  const googleReviewsCode = await window.getNotificationPreview(
    localNotificationSettings,
    "googleReviews",
    undefined,
    t("campaign.notifications.googleReviews.previewText1") ?? "",
    undefined,
    language,
    undefined,
    undefined,
    t("campaign.notifications.googleReviews.previewText1") ?? "",
    4 * 20,
  );

  result.lastOrders = getPureString(lastOrders);
  result.lastRegistrations = getPureString(lastRegistrations);
  result.actualVisitors = getPureString(actualVisitors);
  result.visitorSummary = getPureString(visitorSummary);
  result.registrationSummary = getPureString(registrationSummary);
  result.orderSummary = getPureString(orderSummary);
  result.heureka = getPureString(heureka);
  result.zboziCompanyRating = getPureString(zboziCompanyRating);
  result.zboziProductRating = getPureString(zboziProductRating);
  result.individual = getPureString(individual);
  result.shoptetLastOrders = getPureString(shoptetLastOrders);
  result.shopifyLastOrders = getPureString(shopifyLastOrders);
  result.eshopRychleLastOrders = getPureString(eshopRychleLastOrders);
  result.shoptetCouponCode = getPureString(shoptetCouponCode);
  result.spolehlivaRecenzeShopRating = getPureString(
    spolehlivaRecenzeShopRating,
  );
  result.spolehlivaRecenzeProductRating = getPureString(
    spolehlivaRecenzeProductRating,
  );
  result.googleReviews = getPureString(googleReviewsCode);
  result.trustpilotReviews = getPureString(
    await window.getNotificationPreview(
      localNotificationSettings,
      "trustpilotReviews",
      undefined,
      t("campaign.notifications.trustPilot.previewText1") ?? "",
      undefined,
      language,
      undefined,
      undefined,
      t("campaign.notifications.trustPilot.previewText1") ?? "",
      4 * 20,
    ),
  );

  return result;
};

export const getScriptString = (websiteId: string) =>
  `<script type="text/javascript" website-id="${websiteId}" backend-url="${GlobalVars.SERVER_ENDPOINT}" id="notify-script" src="${GlobalVars.SERVER_ENDPOINT}/pixel/script"></script>`;

export const stringIncludesSubstring = (str: string, substr: string) => {
  return str.includes(substr);
};

export const convertStringToLanguage = (language: string): Language => {
  if (
    language === "cs" ||
    language === "sk" ||
    language === "hu" ||
    language === "pl" ||
    language === "en"
  ) {
    return language;
  } else {
    return isUSA ? "en" : "cs";
  }
};
